import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();
  
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const navItems = [
    { label: t('nav.strategy'), href: '#strategy' },
    { label: t('nav.results'), href: '#results' },
    { label: t('nav.engine'), href: '#engine' },
    { label: t('nav.copyTrading'), href: '#copytrading' },
    { label: t('nav.referral'), href: '#referral' },
    { label: t('nav.help'), href: '#faq' },
    { label: t('nav.terms'), href: '/terms', external: true }
  ];
  
  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-bg-primary/90 backdrop-blur-md py-3 shadow-lg' : 'bg-transparent py-6'
    }`}>
      <div className="container-custom flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src="/logo.png" alt="RegalGain Logo" className="h-8" />
          <img src="/name.png" alt="RegalGain" className="h-6 ml-3" />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-4 xl:space-x-6">
          {navItems.map(item => (
            item.external ? (
              <Link
                key={item.label}
                to={item.href}
                className="font-medium text-sm hover:text-gradient-from transition-colors whitespace-nowrap"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.href}
                className="font-medium text-sm hover:text-gradient-from transition-colors whitespace-nowrap"
              >
                {item.label}
              </a>
            )
          ))}
          <LanguageSwitcher />
          <a href="#copytrading" className="btn btn-accent animate-glowPulse text-sm px-4 py-2 whitespace-nowrap">
            {t('common.startCopying')}
          </a>
        </nav>
        
        {/* Mobile Controls */}
        <div className="flex items-center space-x-4 lg:hidden">
          <LanguageSwitcher />
          <button 
            className="text-white"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      {menuOpen && (
        <div className="lg:hidden bg-bg-secondary/95 backdrop-blur-lg absolute top-full left-0 w-full py-4 shadow-lg">
          <div className="container-custom flex flex-col space-y-4">
            {navItems.map(item => (
              item.external ? (
                <Link 
                  key={item.label}
                  to={item.href}
                  className="font-medium py-2 hover:text-gradient-from transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ) : (
                <a 
                  key={item.label} 
                  href={item.href}
                  className="font-medium py-2 hover:text-gradient-from transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </a>
              )
            ))}
            <a 
              href="#copytrading" 
              className="btn btn-accent w-full text-center"
              onClick={() => setMenuOpen(false)}
            >
              {t('common.startCopying')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;